// KPI aggregation. Reads the orders collection, buckets by ISO week and calendar
// month, and writes small summary docs to the `stats` collection. Charts read
// those (cheap); quarters/years are derived from months client-side.
//
// Recompute (overwrite), not incremental — idempotent and drift-free across
// re-imports, edits and the field-ownership merge. Client-side for now; moves to
// a Node/Function job as history grows toward 30k.

import { collection, getDocs, query, orderBy, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { fromSnapshot } from '../orders/services/ordersService';

const STATS = 'stats';
const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

// ----- period keys -----
const pad2 = (n) => String(n).padStart(2, '0');

export const monthKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

/** ISO-8601 week key "YYYY-Www" (Mon–Sun; wk1 = first week with a Thursday). */
export function isoWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7; // Mon=0
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // Thursday of this week
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round((d - firstThursday) / (7 * 86400000));
  return `${d.getUTCFullYear()}-W${pad2(week)}`;
}

/** Region bucket: UK / EU / US / RoW / Unknown. */
export function regionOf(order) {
  const cc = String(order.shipping?.countryCode || '').toUpperCase();
  if (cc === 'GB' || cc === 'UK') return 'UK'; // GB is canonical; legacy data stored 'UK'
  if (cc === 'US') return 'US';
  if (order.shipping?.isEU) return 'EU';
  // No country at all (many legacy domestic orders) — don't pretend it's RoW.
  if (!cc && !order.shipping?.country) return 'Unknown';
  return 'RoW';
}

// ----- aggregation -----
const emptyMetrics = () => ({ orders: 0, units: 0, revenue: 0, netSales: 0, postage: 0 });
const emptyBucket = () => ({
  ...emptyMetrics(),
  postage: 0,
  discount: 0,
  refund: 0,
  byChannel: {},
  byCountry: {},
  byRegion: {},
});

function addMetrics(map, key, m) {
  if (!key) return;
  const t = map[key] || (map[key] = emptyMetrics());
  t.orders += m.orders;
  t.units += m.units;
  t.revenue += m.revenue;
  t.netSales += m.netSales;
  t.postage += m.postage;
}

/** Build { monthly, weekly } maps of buckets from app orders. */
export function buildBuckets(orders) {
  const monthly = new Map();
  const weekly = new Map();

  orders.forEach((order) => {
    const ordered = order.dates?.ordered;
    if (!(ordered instanceof Date) || Number.isNaN(ordered.getTime())) return;

    // Units exclude embroidery personalisation lines (they're add-ons).
    const units = (order.lineItems || []).reduce(
      (n, li) => n + (li.isEmbroidery ? 0 : num(li.quantity) || 0),
      0
    );
    const m = {
      orders: 1,
      units,
      revenue: num(order.money?.total),
      netSales: num(order.money?.itemsTotal),
      postage: num(order.money?.postage),
    };
    const country = order.shipping?.country || 'Unknown';
    const region = regionOf(order);

    [
      [monthly, monthKey(ordered)],
      [weekly, isoWeekKey(ordered)],
    ].forEach(([map, key]) => {
      const b = map.get(key) || map.set(key, emptyBucket()).get(key);
      b.orders += 1;
      b.units += m.units;
      b.revenue += m.revenue;
      b.netSales += m.netSales;
      b.postage += num(order.money?.postage);
      b.discount += num(order.money?.discount);
      b.refund += num(order.money?.refund);
      addMetrics(b.byChannel, order.source, m);
      addMetrics(b.byCountry, country, m);
      addMetrics(b.byRegion, region, m);
    });
  });

  return { monthly, weekly };
}

async function getAllOrders() {
  const qs = await getDocs(query(collection(db, 'orders'), orderBy('dates.ordered', 'asc')));
  return qs.docs.map(fromSnapshot);
}

async function commitInChunks(docs) {
  for (let i = 0; i < docs.length; i += 400) {
    const batch = writeBatch(db);
    docs.slice(i, i + 400).forEach(({ id, data }) => batch.set(doc(db, STATS, id), data));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}

/** Recompute all weekly/monthly stat docs from the orders collection. */
export async function rebuildStats() {
  const orders = await getAllOrders();
  const { monthly, weekly } = buildBuckets(orders);
  const docs = [];
  monthly.forEach((bucket, key) => docs.push({ id: `m_${key}`, data: { period: 'month', key, ...bucket, updatedAt: serverTimestamp() } }));
  weekly.forEach((bucket, key) => docs.push({ id: `w_${key}`, data: { period: 'week', key, ...bucket, updatedAt: serverTimestamp() } }));
  await commitInChunks(docs);
  return { orders: orders.length, months: monthly.size, weeks: weekly.size };
}

/** Read all stat docs, split into { monthly: {key:doc}, weekly: {key:doc} }. */
export async function getStats() {
  const qs = await getDocs(collection(db, STATS));
  const monthly = {};
  const weekly = {};
  qs.forEach((d) => {
    const x = d.data();
    if (x.period === 'month') monthly[x.key] = x;
    else if (x.period === 'week') weekly[x.key] = x;
  });
  return { monthly, weekly };
}
